import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link as RouterLink } from 'react-router-dom';
import { FaBolt, FaShieldAlt, FaCheckCircle, FaDollarSign, FaFlask, FaTools, FaCog, FaPlug, FaUtensils, FaIndustry, FaOilCan, FaHotel, FaChartLine, FaHandshake, FaArrowDown, FaArrowUp } from 'react-icons/fa';

const Homepage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">
                Industrial Water Treatment Solutions for Africa & Global Markets
              </h1>
              <p className="lead mb-5">
                Delivering high-performance chemical programs, technical expertise, and engineered systems 
                for boilers, cooling towers, RO plants, and industrial water processes.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <Button 
                  variant="warning" 
                  size="lg" 
                  className="cta-button"
                  as={RouterLink}
                  to="/contact"
                >
                  Request a Quote
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg"
                  as={RouterLink}
                  to="/contact"
                >
                  Get Free Water Analysis
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Who We Are */}
      <section className="section-padding">
        <Container>
          <Row>
            <Col lg={12} className="text-center mb-5">
              <h2 className="display-5 fw-bold">Who We Are</h2>
            </Col>
          </Row>
          <Row>
            <Col lg={10} className="mx-auto">
              <p className="lead text-center mb-4">
                NIKDIP Multinational Company is a trusted provider of industrial water treatment solutions, 
                supporting manufacturing, food & beverage, energy, and process industries.
              </p>
              <p className="text-center">
                We combine premium chemical supply, technical service excellence, and engineered systems to help clients achieve:
              </p>
              <Row className="mt-4">
                <Col md={3} className="text-center mb-3">
                  <div className="feature-icon"><FaBolt /></div>
                  <h5>Operational Efficiency</h5>
                </Col>
                <Col md={3} className="text-center mb-3">
                  <div className="feature-icon"><FaShieldAlt /></div>
                  <h5>Equipment Protection</h5>
                </Col>
                <Col md={3} className="text-center mb-3">
                  <div className="feature-icon"><FaCheckCircle /></div>
                  <h5>Regulatory Compliance</h5>
                </Col>
                <Col md={3} className="text-center mb-3">
                  <div className="feature-icon"><FaDollarSign /></div>
                  <h5>Cost Optimization</h5>
                </Col>
              </Row>
              <p className="text-center mt-4">
                With a strong presence in Nigeria and expanding global reach, we deliver solutions aligned with 
                international standards and best practices.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Core Solutions */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Our Core Solutions</h2>
            </Col>
          </Row>
          <Row>
            <Col md={6} lg={3} className="mb-4">
              <Card className="service-card h-100">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon"><FaFlask /></div>
                  <Card.Title as="h4">Chemical Supply</Card.Title>
                  <Card.Text>
                    High-quality industrial water treatment chemicals designed for performance and efficiency:
                  </Card.Text>
                  <ul className="text-start small">
                    <li>Boiler treatment chemicals</li>
                    <li>Cooling tower chemicals</li>
                    <li>Reverse osmosis (RO) chemicals</li>
                    <li>Wastewater treatment chemicals</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <Card className="service-card h-100">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon"><FaTools /></div>
                  <Card.Title as="h4">Technical Services</Card.Title>
                  <Card.Text>
                    We provide expert-driven services that ensure optimal system performance:
                  </Card.Text>
                  <ul className="text-start small">
                    <li>Water analysis & diagnostics</li>
                    <li>Dosing program optimization</li>
                    <li>System audits (Boilers, Cooling Towers, Chillers, RO)</li>
                    <li>KPI-based monitoring and reporting</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <Card className="service-card h-100">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon"><FaCog /></div>
                  <Card.Title as="h4">Engineering & Projects</Card.Title>
                  <Card.Text>
                    End-to-end design and installation of water treatment systems:
                  </Card.Text>
                  <ul className="text-start small">
                    <li>Water softeners</li>
                    <li>Reverse osmosis (RO) plants</li>
                    <li>Demineralization (DM) plants</li>
                    <li>Effluent treatment plants (ETP)</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <Card className="service-card h-100">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon"><FaPlug /></div>
                  <Card.Title as="h4">Equipment & Accessories</Card.Title>
                  <Card.Text>
                    Reliable supply of supporting equipment:
                  </Card.Text>
                  <ul className="text-start small">
                    <li>Dosing pumps</li>
                    <li>Test kits</li>
                    <li>Filtration systems</li>
                    <li>Industrial salt and consumables</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Industries We Serve */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Industries We Serve</h2>
              <p className="lead">We deliver tailored solutions for:</p>
            </Col>
          </Row>
          <Row>
            <Col md={6} lg={4} className="mb-4">
              <div className="text-center">
                <div className="industry-icon"><FaUtensils /></div>
                <h5>Food & Beverage</h5>
              </div>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <div className="text-center">
                <div className="industry-icon"><FaIndustry /></div>
                <h5>Manufacturing & Processing</h5>
              </div>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <div className="text-center">
                <div className="industry-icon"><FaBolt /></div>
                <h5>Power & Energy</h5>
              </div>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <div className="text-center">
                <div className="industry-icon"><FaOilCan /></div>
                <h5>Oil & Gas</h5>
              </div>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <div className="text-center">
                <div className="industry-icon"><FaHotel /></div>
                <h5>Hospitality & Facilities</h5>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose NIKDIP */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Why Choose NIKDIP</h2>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <Row>
                <Col md={6} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3"><FaChartLine /></div>
                    <h5>Technical Expertise</h5>
                    <p>Backed by real plant experience</p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3"><FaBolt /></div>
                    <h5>High-Performance Chemicals</h5>
                    <p>With proven efficiency</p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3"><FaDollarSign /></div>
                    <h5>Cost Optimization</h5>
                    <p>Through reduced consumption and improved system control</p>
                  </div>
                </Col>
                <Col md={6} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3"><FaHandshake /></div>
                    <h5>Reliable Support</h5>
                    <p>With responsive service delivery</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Case Study Highlight */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Case Study Highlight</h2>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <Card className="case-study-card">
                <Card.Body className="p-4">
                  <Badge bg="warning" className="mb-3">Success Story</Badge>
                  <Card.Title as="h4">Boiler Efficiency Improvement - Food Processing Plant</Card.Title>
                  <Card.Text>
                    Optimized chemical dosing and improved feedwater quality resulted in:
                  </Card.Text>
                  <Row className="mt-3 justify-content-center">
                    <Col md={4} className="text-center mb-3">
                      <h5 className="text-success"><FaArrowDown /> Reduced</h5>
                      <p>Blowdown losses</p>
                    </Col>
                    <Col md={4} className="text-center mb-3">
                      <h5 className="text-success"><FaArrowUp /> Improved</h5>
                      <p>Steam generation efficiency</p>
                    </Col>
                    <Col md={4} className="text-center mb-3">
                      <h5 className="text-success"><FaArrowDown /> Lower</h5>
                      <p>Chemical consumption</p>
                    </Col>
                  </Row>
                  <div className="text-center mt-4">
                    <Button variant="outline-primary">View Full Case Studies</Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Optimize Your Water Systems Today</h2>
              <p className="lead mb-4">
                Partner with NIKDIP for reliable, efficient, and scalable water treatment solutions.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  Request a Quote
                </Button>
                <Button variant="outline-light" size="lg">
                  Schedule a Technical Consultation
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Homepage;
